import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import api from '../api';

const ForecastAutomation = () => {
  const [forecastData, setForecastData] = useState(null);
  const [materialNeeds, setMaterialNeeds] = useState(null);
  const [defectStats, setDefectStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadForecastData();
  }, []);

  const loadForecastData = async () => {
    setLoading(true);
    setError('');
    try {
      const [forecastRes, materialRes, defectRes] = await Promise.all([
        api.get('/forecast/production'),
        api.get('/forecast/material-needs'),
        api.get('/forecast/defects'),
      ]);

      setForecastData(forecastRes.data);
      setMaterialNeeds(materialRes.data);
      setDefectStats(defectRes.data);
    } catch (error) {
      console.error('Ошибка загрузки прогноза:', error);
      setError('Не удалось загрузить данные прогноза. Повторите попытку.');
    } finally {
      setLoading(false);
    }
  };

  const getLoadColor = (percentage) => {
    if (percentage >= 80) return 'error';
    if (percentage >= 50) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        <AutoIcon sx={{ mr: 1, fontSize: 40 }} />
        Прогнозирование и автоматизация
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={loadForecastData}>Повторить</Button>}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Активные диваны
              </Typography>
              <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
                {forecastData?.total_active_sofas || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Процент брака
              </Typography>
              <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
                {defectStats?.defect_rate || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #334155 0%, #475569 100%)', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Диванов без ткани
              </Typography>
              <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
                {materialNeeds?.sofas_needing_fabric || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Production Capacity */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingIcon sx={{ mr: 1 }} />
          Производственные возможности
        </Typography>
        <Grid container spacing={2}>
          {forecastData?.production_capacity?.map((item) => (
            <Grid item xs={12} md={6} key={item.model_name}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {item.model_name}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {item.max_sofas} диванов
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Ограничение: {item.limiting_fabric || 'нет'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Worker Load */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1 }} />
          Загрузка работников
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Работник</strong></TableCell>
                <TableCell><strong>Роль</strong></TableCell>
                <TableCell align="center"><strong>Активных задач</strong></TableCell>
                <TableCell align="center"><strong>Загрузка</strong></TableCell>
                <TableCell align="center"><strong>Статус</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forecastData?.worker_load?.map((worker) => (
                <TableRow key={worker.worker_id} hover>
                  <TableCell>{worker.worker_name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        worker.role === 'frame_worker' ? 'Каркасник' :
                        worker.role === 'sewer' ? 'Швея' : 'Обивщик'
                      }
                      size="small"
                      color={
                        worker.role === 'frame_worker' ? 'primary' :
                        worker.role === 'sewer' ? 'secondary' : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={worker.active_tasks} color="default" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={worker.load_percentage}
                          color={getLoadColor(worker.load_percentage)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2">
                        {worker.load_percentage}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {worker.is_available ? (
                      <Chip 
                        label="Свободен" 
                        color="success" 
                        size="small"
                        icon={<CheckIcon />}
                      />
                    ) : (
                      <Chip 
                        label="Занят" 
                        color="warning" 
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Estimated Completion */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 1 }} />
          Прогноз выполнения (срочные диваны)
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Номер</strong></TableCell>
                <TableCell><strong>Модель</strong></TableCell>
                <TableCell align="center"><strong>Дней до готовности</strong></TableCell>
                <TableCell><strong>Прогноз завершения</strong></TableCell>
                <TableCell><strong>Дата доставки</strong></TableCell>
                <TableCell align="center"><strong>Приоритет</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forecastData?.estimated_completion?.map((item) => (
                <TableRow key={item.sofa_number} hover>
                  <TableCell>
                    <strong>{item.sofa_number}</strong>
                  </TableCell>
                  <TableCell>{item.model_name}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${item.days_needed} дней`}
                      color={item.days_needed <= 3 ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.estimated_completion}</TableCell>
                  <TableCell>{item.delivery_date || '-'}</TableCell>
                  <TableCell align="center">
                    {item.is_urgent ? (
                      <Chip 
                        label="СРОЧНО" 
                        color="error" 
                        size="small"
                        icon={<WarningIcon />}
                      />
                    ) : (
                      <Chip label="Обычный" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Material Warnings */}
      {materialNeeds?.fabric_warnings?.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 1 }} />
            Предупреждения по материалам
          </Typography>
          <List>
            {materialNeeds.fabric_warnings.map((warning, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ alignItems: 'flex-start', gap: 1, px: { xs: 0, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <ListItemIcon>
                    <ErrorIcon color={warning.status === 'critical' ? 'error' : 'warning'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={warning.fabric_name}
                    secondary={warning.message}
                  />
                  <Chip 
                    label={`${warning.current_quantity} м`}
                    color={warning.status === 'critical' ? 'error' : 'warning'}
                  />
                </ListItem>
                {index < materialNeeds.fabric_warnings.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Defect Statistics */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 1 }} />
          Статистика брака
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              По работникам
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Работник</TableCell>
                    <TableCell>Роль</TableCell>
                    <TableCell align="right">Браков</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {defectStats?.worker_defects?.map((item) => (
                    <TableRow key={item.worker_id}>
                      <TableCell>{item.worker_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={
                            item.role === 'frame_worker' ? 'Каркасник' :
                            item.role === 'sewer' ? 'Швея' : 'Обстрельщик'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={item.defect_count}
                          color={item.defect_count > 5 ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              По моделям
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Модель</TableCell>
                    <TableCell align="right">Браков</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {defectStats?.model_defects?.map((item) => (
                    <TableRow key={item.model_name}>
                      <TableCell>{item.model_name}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={item.defect_count}
                          color={item.defect_count > 3 ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ForecastAutomation;
